import {Button, Space, Table, Typography} from 'antd';
import {t} from 'i18next';
import {FC} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import useInventoryList from './useInventoryList';
import {TInventory} from '@app/types';
import {LogEvents, useSendEvent} from '@app/utils/logger';
import {useListInventoryQuery} from '@app/redux/services/inventoryService';
import ConfirmActionModal from '@app/components/Modal/ConfirmActionModal';
import {DeleteOutlined, EditOutlined} from '@ant-design/icons';
import InventoryMemoModal from './InventoryMemoModal';

const {Text} = Typography;

const InventoryList: FC = () => {
  const [searchParams] = useSearchParams();
  const sendEvent = useSendEvent();
  const [state, actions] = useInventoryList();
  const navigate = useNavigate();

  const columns = [
    {
      title: t('Registered date'),
      dataIndex: 'date',
      key: 'date',
      sorter: true,
      width: '15%',
    },
    {
      title: t('Registration count'),
      dataIndex: 'medicine_count',
      key: 'medicine_count',
      sorter: true,
      width: '15%',
      render: (value: number) => `${value}件`,
    },
    {
      title: t('Memo'),
      dataIndex: 'memo',
      key: 'memo',
      sorter: true,
      width: '15%',
      textWrap: 'word-break',
    },

    {
      title: t('Operation'),
      dataIndex: 'operation',
      key: 'operation',
      sorter: false,
      width: '15%',
      render: (_: void, record: TInventory) => {
        return (
          <Space>
            <Button
              onClick={() => {
                actions.onMemoModalOpen(record.date);
              }}
              icon={<EditOutlined />}
            >
              <Text className="btn-text">{t('Edit memo')}</Text>
            </Button>
            <Button
              onClick={() => {
                actions.onDeleteModalOpen(record.date);
              }}
              icon={<DeleteOutlined />}
              danger
            >
              <Text type="danger" className="btn-text">
                {t('Delete')}
              </Text>
            </Button>
          </Space>
        );
      },
    },
  ];

  const clickableColumns = columns.map((column) => {
    if (column.key !== 'operation') {
      return {
        ...column,
        onCell: (record: TInventory) => ({
          onClick: () => {
            sendEvent(LogEvents.INVENTORY_LIST_DETAIL_PRESS);
            navigate(`/app/inventories/${record.date}`);
            actions.onMemoModalOpen(record.id);
          },
        }),
      };
    }
    return column;
  });

  const {data: inventoryList, isLoading} = useListInventoryQuery({
    limit: Number(searchParams.get('limit') ?? 10),
    page: Number(searchParams.get('page') ?? 1),
  });

  return (
    <div>
      <Table
        columns={clickableColumns}
        dataSource={inventoryList?.data}
        pagination={{
          total: inventoryList?.meta.total_items,
          pageSize: inventoryList?.meta.items_per_page,
          current: Number(searchParams.get('page') ?? '1'),
          position: ['bottomRight'],
          hideOnSinglePage: false,
        }}
        sticky={false}
        loading={isLoading}
        rowKey={(item) => item.id}
        onChange={actions.onTableChange}
        locale={{
          emptyText: (
            <div style={{textAlign: 'left'}}>
              {t('There is currently no inventory available for display.')}
            </div>
          ),
        }}
      />
      <InventoryMemoModal
        date={state.selectedInventoryDate ?? ''}
        open={state.isMemoModalOpen}
        onCancel={actions.onMemoModalClose}
      />
      <ConfirmActionModal
        open={state.isDeleteModalOpen}
        onConfirm={() => actions.onDeleteConfirm()}
        onCancel={actions.onDeleteModalClose}
        text={t('Is it okay to delete this inventory?')}
      />
    </div>
  );
};
export default InventoryList;
